import { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Container, Divider, Paper, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enqueueNotification } from '../../../store/notification/action';
import styles from './user-profile.module.scss';

const HELP_GUIDANCE_URL = 'https://confluence.jnj.com/spaces/AFIC/pages/142693730/Campaign+Planner+Hub';

export { HELP_GUIDANCE_URL };

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5 MB

export default function UserProfile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.authentication.profile);

  // Avatar upload / removal.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarVersion, setAvatarVersion] = useState(0); // cache-buster
  const [avatarBusy, setAvatarBusy] = useState(false);

  const avatarUrl = user?.wwid
    ? `/api/v2/users/${user.wwid}/avatar?userFullName=${encodeURIComponent(
        user.name || '',
      )}&v=${avatarVersion}`
    : undefined;

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      dispatch(
        enqueueNotification(
          t('userProfile.avatarInvalidType', 'Please choose a PNG, JPG or WebP image.'),
          'error',
          5000,
        ),
      );
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      dispatch(
        enqueueNotification(
          t('userProfile.avatarTooLarge', 'Image is too large (max 5 MB).'),
          'error',
          5000,
        ),
      );
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setAvatarBusy(true);
    axios
      .post('/api/v2/users/me/avatar', formData)
      .then(() => {
        setAvatarVersion(v => v + 1);
        dispatch(
          enqueueNotification(
            t('userProfile.avatarUpdated', 'Profile picture updated.'),
            'success',
            4000,
          ),
        );
      })
      .catch(() =>
        dispatch(
          enqueueNotification(
            t('userProfile.avatarError', 'Could not update the picture. Please try again.'),
            'error',
            5000,
          ),
        ),
      )
      .finally(() => setAvatarBusy(false));
  };

  // Aggregated access (countries / campaign types / products) from a single endpoint.
  const [access, setAccess] = useState<{
    countries: any[];
    campaignTypes: any[];
    products: any[];
  }>({ countries: [], campaignTypes: [], products: [] });
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setAccessLoading(true);
    axios
      .get('/api/v2/auth/access')
      .then(res => {
        if (cancelled) return;
        const data = res.data || {};
        setAccess({
          countries: data.countries || [],
          campaignTypes: data.campaignTypes || [],
          products: data.products || [],
        });
      })
      .catch(() => {
        if (!cancelled) setAccess({ countries: [], campaignTypes: [], products: [] });
      })
      .finally(() => {
        if (!cancelled) setAccessLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const profileType = user?.manualProfile
    ? t('userProfile.profileTypeManual', 'Manual')
    : t('userProfile.profileTypeIconnect', 'iConnect');

  const infoFields = [
    { label: t('userProfile.fullName', 'Full name'), value: user?.name, fullWidth: true },
    { label: t('userProfile.wwid', 'WWID'), value: user?.wwid },
    { label: t('userProfile.email', 'J&J email address'), value: user?.email },
  ];

  return (
    <Container maxWidth={false} style={{ paddingLeft: '128px', paddingRight: '128px', paddingTop: '48px', paddingBottom: '48px'}}>
      <title>{t('userProfile.title', 'My profile')}</title>

      <h1 className={styles.title}>{t('userProfile.title', 'My profile')}</h1>
      <p className={styles.subtitle}>
        {t(
          'userProfile.subtitle',
          'Review your account details, access and role configuration, and manage your personal settings.',
        )}
      </p>

      {/* User information */}
      <Paper elevation={0} className={styles.card}>
        <h2 className={styles.cardTitle}>{t('userProfile.userInformation', 'User information')}</h2>
        <Box className={styles.userInfo}>
          <Box className={styles.avatarColumn}>
            {user && (
              <div className={styles.avatarLarge}>
                <Avatar src={avatarUrl} alt={user.name} />
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
              <span>
                <Button
                  className={styles.changePictureBtn}
                  variant="outlined"
                  size="small"
                  disabled={avatarBusy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('userProfile.changePicture', 'Change picture')}
                </Button>
              </span>
            <span className={styles.avatarCaption}>
              {t('userProfile.pictureHint', 'PNG, JPG or WebP. Square images work best.')}
            </span>
          </Box>

          <Divider orientation="vertical" flexItem className={styles.userInfoDivider} />

          <Box className={styles.fieldsGrid}>
            {infoFields.map(field => (
              <div
                key={field.label}
                className={`${styles.field} ${field.fullWidth ? styles.fieldFull : ''}`}
              >
                <span className={styles.fieldLabel}>{field.label}</span>
                <span className={styles.fieldValue}>{field.value || '—'}</span>
              </div>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Access & Role Configuration */}
      <Paper elevation={0} className={styles.card}>
        <h2 className={styles.cardTitle}>
          {t('userProfile.accessRole', 'Access & Role Configuration')}
        </h2>
        <p className={styles.cardDescription}>
          {t(
            'userProfile.accessRoleDescBefore',
            'This is a read-only overview of what you can access and do in Campaign Planner. To request changes, see',
          )}{' '}
          <a href="#help-guidance" className={styles.inlineLink}>
            {t('userProfile.helpGuidanceLink', 'Help & guidance')}
          </a>{' '}
          {t('userProfile.accessRoleDescAfter', 'below.')}
        </p>

        <Box className={styles.boxRowTop}>
          <Box className={styles.infoBox}>
            <span className={styles.boxLabel}>
              {t('userProfile.profileType', 'Profile type')}
              <Tooltip
                title={t(
                  'userProfile.profileTypeTooltip',
                  'How your profile was created in the system.',
                )}
              >
                <HelpOutlineIcon className={styles.boxLabelIcon} fontSize="inherit" />
              </Tooltip>
            </span>
            <span className={styles.boxValue}>{profileType}</span>
          </Box>

          <Box className={styles.infoBox}>
            <span className={styles.boxLabel}>{t('userProfile.roleType', 'Role type')}</span>
            <span className={styles.boxValue}>{user?.profile?.name || '—'}</span>
          </Box>
        </Box>

        <Box className={styles.boxRowBottom}>
          <Box className={styles.infoBox}>
            <span className={styles.boxLabel}>
              {t('userProfile.countriesOfAccess', 'Countries of access')}
            </span>
            {accessLoading ? (
              <span className={styles.boxValue}>{t('userProfile.loading', 'Loading…')}</span>
            ) : access.countries.length > 0 ? (
              <div className={styles.chips}>
                {access.countries.map((c: any) => (
                  <span key={c.id} className={styles.chip}>
                    {c.name}
                  </span>
                ))}
              </div>
            ) : (
              <span className={styles.boxValue}>{t('userProfile.none', 'None')}</span>
            )}
          </Box>

          <Box className={styles.infoBox}>
            <span className={styles.boxLabel}>
              {t('userProfile.campaignTypeAccess', 'Campaign type access')}
            </span>
            {accessLoading ? (
              <span className={styles.boxValue}>{t('userProfile.loading', 'Loading…')}</span>
            ) : access.campaignTypes.length > 0 ? (
              <div className={styles.chips}>
                {access.campaignTypes.map((ct: any) => (
                  <span key={ct.id} className={styles.chip}>
                    {ct.name}
                  </span>
                ))}
              </div>
            ) : (
              <span className={styles.boxValue}>{t('userProfile.none', 'None')}</span>
            )}
          </Box>

          <Box className={styles.infoBox}>
            <span className={styles.boxLabel}>
              {t('userProfile.productsInScope', 'Products in scope')}
            </span>
            {accessLoading ? (
              <span className={styles.boxValue}>{t('userProfile.loading', 'Loading…')}</span>
            ) : access.products.length > 0 ? (
              <div className={styles.chips}>
                {access.products.map((p: any) => (
                  <span key={p.id} className={styles.chip}>
                    {p.name}
                  </span>
                ))}
              </div>
            ) : (
              <span className={styles.boxValue}>{t('userProfile.none', 'None')}</span>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Help & Guidance */}
      <Paper elevation={0} className={styles.card} id="help-guidance">
        <h2 className={styles.cardTitle}>{t('userProfile.helpGuidance', 'Help & Guidance')}</h2>
        <p className={styles.helpQuestion}>
          {t('userProfile.helpQuestion', 'Need additional access or changes to your profile settings?')}
        </p>
        <p className={styles.cardDescription}>
          {t(
            'userProfile.helpText',
            'Your role, countries and campaign access are managed centrally. Use the link below to learn how roles work and how to request a change.',
          )}
        </p>
        <Button
          className={styles.helpButton}
          variant="contained"
          component="a"
          href={HELP_GUIDANCE_URL}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<InfoIcon />}
        >
          {t('userProfile.learnMore', 'Learn more about user roles and how to request access')}
        </Button>
      </Paper>
    </Container>
  );
}
